/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import * as XLSX from 'xlsx';
import {SpecialOrderService} from '../../../api/ms-specialorders/specialorder/specialorder.service';
import {SpecialOrderPositionValidationModel} from '../../../api/ms-specialorders/contracts/special-order-position-validation.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ExcelUploadModalComponent} from './excel-upload-modal/excel-upload-modal.component';
import {ExcelUploadService} from './excel-upload.service';
import {Subscription} from 'rxjs';
import {SpecialOrderPositionValidationResultModel} from '../../../api/ms-specialorders/contracts/special-order-position-validation-result.model';
import {ConfirmModalComponent} from '../../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {ConfirmModal} from '../../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {AlertNotificationSeverity} from '../../../alert-notification-severity.model';
import {IS_TEST_ENVIRONMENT} from '../../../shared/utils/is-test-environment';

type ExcelData = any[][];
const ExcelHeader = [
    'Material',
    'Quantity',
    'Model type',
    'LOT number',
    'MCDR number',
    'IGEF number',
    'Colour code'
];

@Component({
    selector: 'app-excel-upload',
    templateUrl: './excel-upload.component.html',
    styleUrls: ['./excel-upload.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ExcelUploadComponent implements OnDestroy {
    @Input() supplyingPackingPlantCode: string;
    @Input() disabled = false;
    @Output() validMaterialsEmitter = new EventEmitter<SpecialOrderPositionValidationResultModel[]>();
    @Input() materialsItemsLength: number;
    @Input() maxNumberOfMaterials: number;

    private subscriptions = new Subscription();

    constructor(private service: SpecialOrderService,
                private modalService: NgbModal,
                private excelService: ExcelUploadService,
                private ngZone: NgZone) {
        if (IS_TEST_ENVIRONMENT) {
            (window as any).ExcelUpload = (files: FileList): void => this.ngZone.run(
                    () => this.onFileChange({
                        target: {
                            value: null,
                            files
                        }
                    }));
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    onFileChange(fileEvent: any): void {
        const target: DataTransfer = fileEvent.target;
        if (target.files.length !== 1) {
            throw new Error('Cannot use multiple files');
        }

        const reader: FileReader = new FileReader();

        reader.onload = (loadEvent: any): void => {
            const byteString: string = loadEvent.target.result;

            let workbook: XLSX.WorkBook;
            try {
                workbook = XLSX.read(byteString, {type: 'binary'});
            } catch (Error) {
                this.showModal('The uploaded Excel file has the wrong format.',
                        AlertNotificationSeverity.ERROR);
                return;
            }

            const worksheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[worksheetName];

            const data: ExcelData = XLSX.utils.sheet_to_json(worksheet, {header: 1});

            if (!this.validateExcelStructure(data)) {
                return;
            }

            const materials = this.mapWorksheetDataToPositionValidationModel(data);

            const totalMaterials = materials.length + this.materialsItemsLength;

            if (totalMaterials > this.maxNumberOfMaterials) {
                const message = 'The number of materials exceeded the limit of ' + this.maxNumberOfMaterials + ' materials per SPO.';
                this.showMaxNumberMaterialsModal(message);
                return;
            }

            this.service.validateSpecialOrderMaterials({
                specialOrderMaterials: materials,
                supplyingPackingPlantCode: this.supplyingPackingPlantCode
            })
                    .subscribe(response => {
                        const validMaterials = response.filter(positionValidation => positionValidation.valid);
                        const invalidMaterials = response.filter(positionValidation => !positionValidation.valid);
                        const hasInvalidMaterials = invalidMaterials.length > 0;

                        if (hasInvalidMaterials) {
                            const instance: ExcelUploadModalComponent =
                                    this.modalService.open(ExcelUploadModalComponent, {centered: true}).componentInstance;

                            this.subscriptions.add(
                                    instance.validMaterialsEmitter.subscribe((m: SpecialOrderPositionValidationResultModel[]) => {
                                        if (m.length > 0) {
                                            this.validMaterialsEmitter.emit(m);
                                        }
                                    }));

                            this.excelService.validMaterials = validMaterials;
                            this.excelService.invalidMaterials = invalidMaterials;
                        } else {
                            this.validMaterialsEmitter.emit(validMaterials);
                        }
                    }, error => {
                        const message: string = error.status === 0 ? 'Unexpected error' : (error.error.message || error.statusText);

                        this.showMaxNumberMaterialsModal(message);

                    });
        };

        reader.readAsBinaryString(target.files[0]);

        fileEvent.target.value = '';
    }

    /**
     * Show a error modal that show the error, after upload excel file with a max number of materials
     * @param message the {@link message} that represents the string error message.
     * @return void.
     */
    showMaxNumberMaterialsModal(message: string): void {
        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent, {
            centered: true
        }).componentInstance;
        confirmModalInstance.initModal({
            titleText: 'The number of materials exceeded the limit',
            subtitleText: `${message}`,
            footer: `Please request less materials.`,
            alertNotificationSeverity: AlertNotificationSeverity.ERROR,
            secondaryButtonText: 'Cancel'
        } as ConfirmModal);
    }

    /**
     * Validates if the Excel file being uploaded has the correct structure.
     * Also shows a {@link ConfirmModalComponent} if the file has an incorrect structure.
     * @param data the {@link ExcelData} that represents the file being uploaded.
     * @return false if the file has an incorrect structure, true otherwise.
     */
    validateExcelStructure(data: ExcelData): boolean {
        if (data.length < 1 || data[0].length !== 7) {
            this.showModal('The uploaded Excel file has the wrong format.',
                    AlertNotificationSeverity.ERROR);
            return false;
        }

        const actualHeader = JSON.stringify(data[0]);
        const expectedHeader = JSON.stringify(ExcelHeader);

        if (actualHeader !== expectedHeader) {
            this.showModal('The uploaded Excel file has the wrong format.',
                    AlertNotificationSeverity.ERROR);
            return false;
        }

        if (data.length < 2) {
            this.showModal('The uploaded Excel file doesn\'t have any materials.',
                    AlertNotificationSeverity.WARNING);
            return false;
        }

        return true;
    }

    mapWorksheetDataToPositionValidationModel(data: ExcelData): SpecialOrderPositionValidationModel[] {
        const [, ...lines]: ExcelData = data;
        const materials = lines.map((line, index) => ({
            // We're skipping the header line (+1) and index is 0-based (+1), hence the "index + 2"
            row: index + 2,
            number: line[0] || '',
            orderQuantity: line[1] || '',
            modelType: line[2] ? line[2].toUpperCase() : '',
            lotNumber: line[3] || '',
            mcdrNumber: line[4] || '',
            igefNumber: line[5] ? line[5].toUpperCase() : '',
            colourCode: line[6] ? line[6].toUpperCase() : ''
        } as SpecialOrderPositionValidationModel));

        return materials;
    }

    /**
     * Wrapper to show an error confirmation modal.
     * @param text details about the error.
     */
    private showModal(text: string, severity: AlertNotificationSeverity): void {
        this.modalService.open(ConfirmModalComponent, {centered: true})
                .componentInstance
                .initModal({
                    titleText: 'Invalid Excel file',
                    subtitleText: text,
                    alertNotificationSeverity: severity,
                    primaryButtonText: null,
                    secondaryButtonText: 'Close'
                } as ConfirmModal);
    }
}
